import { forwardRef, type HTMLAttributes } from "react";
import type { Agent } from "./agent-card.js";
import { AgentCard } from "./agent-card.js";
import { RatingBadge } from "./rating-badge.js";
import { cn } from "./lib/cn.js";

/**
 * Generic leaderboard. Dumb — receives rows, renders them with rank gutters
 * and rating columns. The 8-column rule from leaderboard/architecture.md §13
 * is enforced visually: rank, Agent (handle + league + archetype), rating,
 * delta, matches, win%. Five visible columns + a rank gutter = six total.
 *
 * Empty + loading states are handled here so app pages don't repeat the UI.
 */

export interface LeaderboardRow extends Agent {
 rank: number;
 matches?: number;
 winRate?: number;
}

export interface LeaderboardTableProps extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
 rows: LeaderboardRow[];
 loading?: boolean;
 emptyMessage?: string;
 /** Caller can override what shows when loading=true (default: 6 skeleton rows). */
 loadingPlaceholder?: number;
}

const mono: React.CSSProperties = {
 fontFamily: "var(--pp-font-family)",
 fontVariantNumeric: "tabular-nums",
};

export const LeaderboardTable = forwardRef<HTMLDivElement, LeaderboardTableProps>(
 ({ rows, loading, emptyMessage, loadingPlaceholder = 6, className, ...props }, ref) => {
 if (loading) {
 return (
 <div
 ref={ref}
 className={cn("flex flex-col gap-2", className)}
 aria-busy="true"
 {...props}
 >
 {Array.from({ length: loadingPlaceholder }).map((_, i) => (
 <div
 key={i}
 className="h-14 bg-[var(--pp-bg-strong)] border border-[var(--pp-border)] animate-pulse"
 />
 ))}
 </div>
 );
 }

 if (rows.length === 0) {
 return (
 <div
 ref={ref}
 className={cn(
 "py-12 text-center text-[var(--pp-text-muted)] border border-dashed border-[var(--pp-border)] ",
 className,
 )}
 {...props}
 >
 {emptyMessage ?? "No Agents yet — be the first to queue."}
 </div>
 );
 }

 return (
 <div
 ref={ref}
 role="table"
 aria-label="Leaderboard"
 className={cn(
 "border border-[var(--pp-border)] overflow-hidden",
 className,
 )}
 {...props}
 >
 <div
 role="row"
 className="grid grid-cols-[3rem_minmax(180px,1fr)_minmax(120px,auto)_4rem_4rem] gap-3 px-3 py-2 border-b border-[var(--pp-border)] bg-[var(--pp-bg-strong)]"
 style={{
 ...mono,
 fontSize: "var(--pp-font-sm)",
 textTransform: "uppercase",
 letterSpacing: "var(--pp-tracking-caps)",
 color: "var(--pp-text-muted)",
 }}
 >
 <span role="columnheader">Rank</span>
 <span role="columnheader">Agent</span>
 <span role="columnheader">Rating</span>
 <span role="columnheader" style={{ textAlign: "right" }}>
 Matches
 </span>
 <span role="columnheader" style={{ textAlign: "right" }}>
 Win %
 </span>
 </div>

 {rows.map((row) => (
 <div
 key={row.handle}
 role="row"
 className={cn(
 "grid grid-cols-[3rem_minmax(180px,1fr)_minmax(120px,auto)_4rem_4rem] gap-3 px-3 py-2 items-center",
 "border-b border-[var(--pp-border)] last:border-b-0",
 "bg-[transparent] hover:bg-[var(--pp-bg-strong)]",
 )}
 >
 <span role="cell" style={{ ...mono, color: "var(--pp-text-muted)" }}>
 {String(row.rank).padStart(2, "0")}
 </span>
 <span role="cell" style={{ minWidth: 0 }}>
 <AgentCard
 agent={row}
 layout="row"
 style={{ background: "transparent", padding: 0 }}
 />
 </span>
 <span role="cell">
 <RatingBadge
 rating={row.rating}
 ci={row.ratingCI}
 delta={row.ratingDelta}
 />
 </span>
 <span
 role="cell"
 style={{ ...mono, textAlign: "right", color: "var(--pp-text-muted)" }}
 >
 {row.matches ?? "—"}
 </span>
 <span
 role="cell"
 style={{ ...mono, textAlign: "right", color: "var(--pp-text-muted)" }}
 >
 {row.winRate !== undefined ? `${Math.round(row.winRate * 100)}%` : "—"}
 </span>
 </div>
 ))}
 </div>
 );
 },
);
LeaderboardTable.displayName = "LeaderboardTable";
